import React, { useCallback, useEffect, useRef, useState } from "react";
import Icon from "./Icon";
import StoreLinks from "./StoreLinks";
import { COMPANION } from "../config/app";
import { canMeasure, hasMotionSensors, requestMotionPermission, detectPlatform } from "../utils/platform";

/**
 * On-device measurement, run entirely in the browser. Nothing leaves the
 * phone — no frame is uploaded, no reading is transmitted.
 *
 * -----------------------------------------------------------------------
 * WHAT A BROWSER CAN AND CANNOT MEASURE
 *
 * These two halves have very different accuracy, and the UI says so
 * because pretending otherwise would produce confident wrong numbers.
 *
 * PAN SPEED is genuinely measurable. DeviceOrientation reports real
 * rotation in degrees, so differentiating it gives actual degrees per
 * second. That is a true reading from a real sensor, and it's the thing a
 * settings table can never give you — the table says "pan slowly", this
 * tells you whether what you just did was slow.
 *
 * LIGHT is a different story. Pixel brightness is not scene brightness,
 * because the camera's auto-exposure is continuously adjusting to keep the
 * image mid-grey — point a phone at a dim room and a bright street and the
 * pixels come back similar. To recover the actual light you need the
 * exposure the camera chose, and that metadata is only exposed on some
 * Android browsers via MediaTrackSettings. Safari on iOS does not expose
 * it at all.
 *
 * So this reports one of three tiers:
 *   metered   — exposure metadata available, EV computed from it
 *   relative  — no metadata, brightness comparison only, no EV
 *   none      — no camera access
 *
 * On iOS the honest answer is "relative", and the honest recommendation is
 * a native app that can read the sensor properly. That's not a sales line;
 * it's the actual limitation, which is why it's stated even though it
 * points away from this page.
 * -----------------------------------------------------------------------
 */

// A phone's main camera is typically near f/1.8. We don't know WHICH phone,
// so this is an assumption, and every EV derived from it is labelled as an
// estimate rather than a measurement.
const ASSUMED_APERTURE = 1.8;

function evFromExposure(exposureTimeUnits, iso) {
  // MediaTrackSettings.exposureTime is specified in 100-microsecond units.
  const seconds = (exposureTimeUnits * 100) / 1e6;
  if (!seconds || !iso) return null;
  const ev = Math.log2((ASSUMED_APERTURE * ASSUMED_APERTURE) / seconds) - Math.log2(iso / 100);
  return Math.round(ev * 10) / 10;
}

function ndForGap(stops) {
  if (stops <= 0.5) return "No ND needed";
  const options = [
    [1, "ND2"], [2, "ND4"], [3, "ND8"], [4, "ND16"],
    [5, "ND32"], [6, "ND64"], [7, "ND128"], [8, "ND256"], [10, "ND1000"],
  ];
  const pick = options.find(([s]) => s >= stops - 0.35) || options[options.length - 1];
  return pick[1];
}

export default function SceneAnalyzer({ recipe, camera }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);

  const [platform, setPlatform] = useState("unknown");
  const [lightState, setLightState] = useState({ status: "idle", tier: null, ev: null, brightness: null, error: null });
  const [pan, setPan] = useState({ status: "idle", degPerSec: 0, peak: 0, error: null });

  useEffect(() => setPlatform(detectPlatform()), []);

  // --- light ---------------------------------------------------------------
  const stopCamera = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  // Releasing the camera on unmount is not optional — a stream left running
  // keeps the hardware indicator lit, which reads to a person as the site
  // still watching them after they navigated away.
  useEffect(() => stopCamera, [stopCamera]);

  const startCamera = useCallback(async () => {
    if (!canMeasure()) {
      setLightState({
        status: "unsupported",
        tier: "none",
        ev: null,
        brightness: null,
        error: window.isSecureContext === false
          ? "Camera access needs a secure (https) connection."
          : "This browser doesn't offer camera access.",
      });
      return;
    }

    setLightState((s) => ({ ...s, status: "starting", error: null }));

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }

      const track = stream.getVideoTracks()[0];

      const sample = () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas || video.readyState < 2) {
          rafRef.current = requestAnimationFrame(sample);
          return;
        }

        // A 32x32 downsample is plenty for a mean-luminance figure and
        // costs almost nothing per frame.
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        canvas.width = 32;
        canvas.height = 32;
        ctx.drawImage(video, 0, 0, 32, 32);
        const { data } = ctx.getImageData(0, 0, 32, 32);
        let sum = 0;
        for (let i = 0; i < data.length; i += 4) {
          // Rec. 709 luma coefficients.
          sum += 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
        }
        const brightness = Math.round((sum / (data.length / 4) / 255) * 100);

        const settings = track && track.getSettings ? track.getSettings() : {};
        const ev = settings.exposureTime && settings.iso
          ? evFromExposure(settings.exposureTime, settings.iso)
          : null;

        setLightState({
          status: "running",
          tier: ev != null ? "metered" : "relative",
          ev,
          brightness,
          error: null,
        });

        rafRef.current = requestAnimationFrame(sample);
      };

      rafRef.current = requestAnimationFrame(sample);
    } catch (err) {
      const denied = err && (err.name === "NotAllowedError" || err.name === "SecurityError");
      setLightState({
        status: "error",
        tier: "none",
        ev: null,
        brightness: null,
        error: denied
          ? "Camera permission was declined. You can allow it in your browser's site settings and try again."
          : "Couldn't open the camera on this device.",
      });
    }
  }, []);

  // --- pan speed -----------------------------------------------------------
  useEffect(() => {
    if (pan.status !== "running") return undefined;

    let lastAlpha = null;
    let lastAt = null;

    const onOrientation = (e) => {
      if (e.alpha == null) return;
      const now = performance.now();
      if (lastAlpha != null && lastAt != null) {
        // Alpha wraps at 360; take the shorter way round or a pan across
        // north reads as a 350°/s spike.
        let delta = e.alpha - lastAlpha;
        if (delta > 180) delta -= 360;
        if (delta < -180) delta += 360;
        const dt = (now - lastAt) / 1000;
        if (dt > 0.02) {
          const rate = Math.abs(delta / dt);
          setPan((p) => ({
            ...p,
            degPerSec: Math.round(rate * 10) / 10,
            peak: Math.max(p.peak, Math.round(rate * 10) / 10),
          }));
          lastAlpha = e.alpha;
          lastAt = now;
        }
      } else {
        lastAlpha = e.alpha;
        lastAt = now;
      }
    };

    window.addEventListener("deviceorientation", onOrientation);
    return () => window.removeEventListener("deviceorientation", onOrientation);
  }, [pan.status]);

  const startPan = useCallback(async () => {
    if (!hasMotionSensors()) {
      setPan({ status: "unsupported", degPerSec: 0, peak: 0, error: "This device doesn't report orientation." });
      return;
    }
    const permission = await requestMotionPermission();
    if (permission !== "granted") {
      setPan({
        status: "error",
        degPerSec: 0,
        peak: 0,
        error: "Motion access was declined. On iOS it can be re-enabled under Settings → Safari → Motion & Orientation Access.",
      });
      return;
    }
    setPan({ status: "running", degPerSec: 0, peak: 0, error: null });
  }, []);

  // --- derived -------------------------------------------------------------
  const assumedEv = recipe && recipe.lightingExposureStops != null ? recipe.lightingExposureStops : null;
  const measuredEv = lightState.ev;
  const evDelta = measuredEv != null && assumedEv != null ? Math.round((measuredEv - assumedEv) * 10) / 10 : null;

  // A 24fps 180° pan crossing the frame in about 6 seconds is the usual
  // comfortable ceiling; faster than that and the strobing becomes visible.
  const panTarget = recipe && recipe.settings ? Math.round((60 / (recipe.settings.fps || 24)) * 10) : 12;
  const panVerdict =
    pan.status !== "running" ? null
      : pan.peak === 0 ? "Move the phone as you would the camera."
      : pan.peak <= panTarget ? "Within a comfortable range for this frame rate."
      : `Faster than this frame rate holds cleanly. Aim under ${panTarget}°/s.`;

  return (
    <div className="analyzer">
      <p className="analyzer-intro">
        Two measurements your phone can actually take, run entirely on the device. No frame is
        uploaded and no reading leaves your phone.
      </p>

      {/* ---------------- pan ---------------- */}
      <section className="analyzer-module">
        <h3><Icon name="pan" /> Pan speed</h3>
        <p className="analyzer-note">
          Uses the orientation sensor, so this is a real reading rather than an estimate. Start it,
          then pan the phone the way you intend to move the camera.
        </p>

        {pan.status !== "running" && (
          <button type="button" className="analyzer-button" onClick={startPan}>
            Measure a pan
          </button>
        )}

        {pan.status === "running" && (
          <div className="readout">
            <div className="readout-main">
              <span className="readout-value data">{pan.degPerSec}</span>
              <span className="readout-unit">°/sec now</span>
            </div>
            <div className="readout-sub data">
              peak {pan.peak}°/s · target under {panTarget}°/s at {recipe?.settings?.fps || 24}fps
            </div>
            {panVerdict && <p className="readout-verdict">{panVerdict}</p>}
          </div>
        )}

        {pan.error && <p className="analyzer-error">{pan.error}</p>}
      </section>

      {/* ---------------- light ---------------- */}
      <section className="analyzer-module">
        <h3><Icon name="light" /> Light</h3>

        {lightState.status === "idle" && (
          <>
            <p className="analyzer-note">
              This recipe assumes {recipe?.lightingName || "the selected condition"}
              {assumedEv != null && <>, about EV {assumedEv}</>}. Point the camera at your scene to
              see how close that assumption is.
            </p>
            <button type="button" className="analyzer-button" onClick={startCamera}>
              Use the camera
            </button>
          </>
        )}

        {lightState.status === "starting" && <p className="analyzer-note">Opening the camera…</p>}

        <video
        ref={videoRef}
        className={`analyzer-video ${lightState.status === "running" ? "is-live" : ""}`}
        playsInline
        muted
        aria-hidden="true"
      />
        <canvas ref={canvasRef} className="analyzer-canvas" aria-hidden="true" />

        {lightState.status === "running" && lightState.tier === "metered" && (
          <div className="readout">
            <div className="readout-main">
              <span className="readout-value data">EV {measuredEv}</span>
              <span className="readout-unit">measured</span>
            </div>
            {evDelta != null && (
              <div className="readout-sub data">
                {Math.abs(evDelta) < 0.5
                  ? `matches the ${recipe.lightingName?.toLowerCase()} assumption`
                  : `${Math.abs(evDelta)} stops ${evDelta > 0 ? "brighter" : "darker"} than this page assumes`}
              </div>
            )}
            {evDelta != null && Math.abs(evDelta) >= 0.5 && recipe?.settings && (
              <p className="readout-verdict">
                At {recipe.settings.shutter} and ISO {recipe.settings.isoLow || "base"}, that puts the
                filter closer to <strong>{ndForGap((recipe.settings.ndRequiredStops || 0) + evDelta)}</strong>{" "}
                than the {recipe.settings.ndFilter} on this page.
              </p>
            )}
            <p className="analyzer-caveat">
              Derived from the exposure your camera chose, assuming an f/{ASSUMED_APERTURE} lens. It's a
              good estimate, not a calibrated meter reading.
            </p>
          </div>
        )}

        {lightState.status === "running" && lightState.tier === "relative" && (
          <div className="readout">
            <div className="readout-main">
              <span className="readout-value data">{lightState.brightness}%</span>
              <span className="readout-unit">relative brightness</span>
            </div>
            <p className="analyzer-caveat">
              This browser doesn't expose the camera's exposure settings, so an absolute EV can't be
              calculated here, only how bright one scene is compared to another. That's a limit of
              the browser, not the phone{platform === "ios" ? ": Safari doesn't expose this metadata to web pages at all" : ""}.
            </p>
          </div>
        )}

        {lightState.error && <p className="analyzer-error">{lightState.error}</p>}
      </section>

      {/* ---------------- handoff ---------------- */}
      <section className="analyzer-upgrade">
        <p className="eyebrow">For a real reading</p>
        <p className="analyzer-upgrade-text">
          {COMPANION.name} reads the sensor properly rather than inferring from a web preview, and
          labels every reading by how much to trust it. It knows this camera's actual aperture
          instead of assuming one.
        </p>
        <StoreLinks compact />
      </section>
    </div>
  );
}
